/**
 * Calcolatore IVA centralizzato — Task #1484.
 *
 * SINGLE SOURCE OF TRUTH per scorporo/applicazione IVA su tutte le righe
 * documento (vendite POS, acquisti, DDT, listini, fattura). Sostituisce le
 * formule lineari `taxable = total / (1 + rate/100)` sparse nei service:
 * sono CORRETTE solo per le aliquote standard, ma SBAGLIATE per art.36
 * (margine) e art.74 (monofase).
 *
 * 9 modalità coperte (corrispondenti a `pos_vat_calculation_mode`):
 *   - standard:           IVA ordinaria scorporata o applicata.
 *   - excluded_art15:     escluso da base imponibile (interessi moratori, penalità).
 *   - monofase_art74:     IVA assolta a monte da editore/Monopoli/gestore →
 *                         taxable=prezzo, vat=0, total=prezzo. Dicitura SDI.
 *   - margin_analytic:    art.36 c.1 — margin = max(0, sale-purchase),
 *                         vat = margin × rate / (100+rate).
 *   - margin_global:      art.36 c.6 — riga registrata SENZA IVA esposta;
 *                         IVA si liquida per periodo via aggregazione.
 *   - margin_forfait:     art.36 c.5 — margin = forfait_pct% × sale_price;
 *                         vat = margin × rate / (100+rate).
 *   - reverse_charge:     emittente espone solo imponibile (vat=0); IVA è
 *                         a carico del cessionario (autofatturazione).
 *   - exempt_art10:       esente art.10 — vat=0, dicitura.
 *   - non_taxable_art7:   non soggetta art.7-7septies — vat=0, dicitura.
 *
 * Riferimenti normativi:
 *   - DPR 633/1972 art. 7-15
 *   - DL 41/1995 art. 36
 *   - DPR 633/1972 art. 74 (incl. 74-bis/quinquies)
 *
 * Convenzione I/O:
 *   - Input prezzo SEMPRE LORDO se `inputIsGross=true` (default cassa retail).
 *   - Sconto applicato sul lordo (preserva esatto-al-centesimo per scontrino).
 *   - Aggregati arrotondati a 2 decimali, unitari a 4 (compat SDI 2.2.2).
 *   - Modalità a IVA non esposta (margine, monofase, esente, reverse, art15)
 *     restituiscono `lineNet = lineGross` (prezzo cliente) e `lineVat = 0`.
 *     L'eventuale IVA "interna" del margine è in `marginVat` per la
 *     liquidazione, non per esporla in fattura.
 */

import { z } from 'zod';

export const VAT_CALCULATION_MODES = [
  'standard',
  'excluded_art15',
  'monofase_art74',
  'margin_analytic',
  'margin_global',
  'margin_forfait',
  'reverse_charge',
  'exempt_art10',
  'non_taxable_art7',
] as const;
export type VatCalculationMode = (typeof VAT_CALCULATION_MODES)[number];

export const VatRateMetaSchema = z.object({
  code: z.string().min(1),
  rate: z.number().nonnegative(),
  calculation_mode: z.enum(VAT_CALCULATION_MODES),
  margin_forfait_pct: z.number().nullable().optional(),
  vat_billing_note: z.string().nullable().optional(),
  /**
   * Task #3198 — testo del riferimento normativo RISOLTO a monte via
   * `pos_vat_rates.default_rif_id → natura_iva_map.riferimento_normativo`
   * (sostituisce il legacy free-text `legal_reference`, colonna droppata).
   */
  riferimento_normativo: z.string().nullable().optional(),
  nature_code: z.string().nullable().optional(),
});
export type VatRateMeta = z.infer<typeof VatRateMetaSchema>;

export const VatCalculationInputSchema = z.object({
  quantity: z.number(),
  unitPrice: z.number(),
  discountPct: z.number().min(0).max(100).default(0),
  inputIsGross: z.boolean().default(true),
  rateMeta: VatRateMetaSchema,
  /**
   * Prezzo unitario di acquisto (PA). Obbligatorio per `margin_analytic`.
   * Ignorato dalle altre modalità.
   */
  purchasePriceUnit: z.number().nullable().optional(),
});
export type VatCalculationInput = z.infer<typeof VatCalculationInputSchema>;

export interface VatCalculationResult {
  /** Modalità applicata (eco dell'input). */
  mode: VatCalculationMode;
  /** Aliquota effettiva applicata (può differire dal rate dichiarato per modalità a IVA=0). */
  effectiveRate: number;
  /** Codice natura SDI da emettere su XML (null per standard imponibile). */
  natureCode: string | null;
  /** Dicitura obbligatoria SDI (null per standard imponibile). */
  vatBillingNote: string | null;
  /** Prezzo unitario lordo cristallizzato (4 decimali). */
  unitPriceGross: number;
  /** Prezzo unitario netto cristallizzato (4 decimali). */
  unitPriceNet: number;
  /** IVA per unità cristallizzata (4 decimali). */
  vatAmountUnit: number;
  /** Totale riga lordo (2 decimali). */
  lineGross: number;
  /** Imponibile riga (2 decimali). 0 per modalità a IVA non esposta. */
  lineNet: number;
  /** IVA esposta in fattura (2 decimali). 0 per margine/monofase/esente/reverse/art15. */
  lineVat: number;
  /** Sconto applicato sulla riga (€, 2 decimali). */
  discountAmount: number;
  /**
   * Margine calcolato (€, 2 decimali). Valorizzato solo per modalità
   * `margin_analytic` e `margin_forfait`. Il `margin_global` aggrega per
   * periodo, non per riga, quindi qui resta 0.
   */
  margin: number;
  /**
   * IVA implicita sul margine (€, 2 decimali). NON va esposta in fattura
   * — serve per la liquidazione periodica del regime margine.
   */
  marginVat: number;
}

const r2 = (n: number): number => Math.round(n * 100) / 100;
const r4 = (n: number): number => Math.round(n * 10000) / 10000;

function clampQty(q: number): number {
  return Number.isFinite(q) && q > 0 ? q : 0;
}

function clampDiscount(d: number): number {
  if (!Number.isFinite(d)) return 0;
  return Math.min(100, Math.max(0, d));
}

function buildBaseRoundings(
  unitGross: number,
  unitNet: number,
  q: number,
  discountPct: number,
): { lineGross: number; discountAmount: number; vatUnit: number } {
  const factor = 1 - discountPct / 100;
  const grossRaw = q * unitGross * factor;
  const lineGross = q > 0 ? r2(grossRaw) : 0;
  const discountAmount = q > 0 ? r2(q * unitGross * (discountPct / 100)) : 0;
  return { lineGross, discountAmount, vatUnit: unitGross - unitNet };
}

/**
 * Cuore del calcolatore. Input typed via zod; eccezione se input non valido.
 *
 * NB: i moduli BE/FE chiamanti DEVONO caricare `rateMeta` da `pos_vat_rates`
 * via FK esplicita (non più `vat_code` testuale). La libreria non fa più
 * lookup interno per evitare cache/IO da una funzione pure.
 */
export function calculateVat(input: VatCalculationInput): VatCalculationResult {
  const parsed = VatCalculationInputSchema.parse(input);
  const meta = parsed.rateMeta;
  const q = clampQty(parsed.quantity);
  const discountPct = clampDiscount(parsed.discountPct);
  const up = Number.isFinite(parsed.unitPrice) ? parsed.unitPrice : 0;
  const ratePct = Math.max(0, meta.rate);
  const factor = 1 - discountPct / 100;

  // Helper: build "no-VAT-exposed" result (margine/monofase/esente/reverse/art15).
  // Il prezzo cliente resta intatto (lineNet = lineGross), niente IVA in fattura.
  const noExposed = (extra?: Partial<VatCalculationResult>): VatCalculationResult => {
    const base = buildBaseRoundings(up, up, q, discountPct);
    return {
      mode: meta.calculation_mode,
      effectiveRate: 0,
      natureCode: meta.nature_code ?? null,
      vatBillingNote: meta.vat_billing_note ?? null,
      unitPriceGross: r4(up),
      unitPriceNet: r4(up),
      vatAmountUnit: 0,
      lineGross: base.lineGross,
      lineNet: base.lineGross,
      lineVat: 0,
      discountAmount: base.discountAmount,
      margin: 0,
      marginVat: 0,
      ...extra,
    };
  };

  switch (meta.calculation_mode) {
    case 'standard': {
      const unitGross = parsed.inputIsGross
        ? up
        : (ratePct > 0 ? up * (1 + ratePct / 100) : up);
      const unitNet = ratePct > 0 ? unitGross / (1 + ratePct / 100) : unitGross;
      const vatUnit = unitGross - unitNet;
      const lineGross = q > 0 ? r2(q * unitGross * factor) : 0;
      const lineNet = ratePct > 0 ? r2(lineGross / (1 + ratePct / 100)) : lineGross;
      const lineVat = r2(lineGross - lineNet);
      const discountAmount = q > 0 ? r2(q * unitGross * (discountPct / 100)) : 0;
      return {
        mode: 'standard',
        effectiveRate: ratePct,
        natureCode: null,
        vatBillingNote: null,
        unitPriceGross: r4(unitGross),
        unitPriceNet: r4(unitNet),
        vatAmountUnit: r4(vatUnit),
        lineGross,
        lineNet,
        lineVat,
        discountAmount,
        margin: 0,
        marginVat: 0,
      };
    }

    case 'excluded_art15':
    case 'exempt_art10':
    case 'non_taxable_art7':
    case 'monofase_art74':
    case 'reverse_charge':
    case 'margin_global': {
      // Tutte trattano la riga come IVA non esposta: lineNet=lineGross, vat=0.
      // La distinzione sta in natureCode + vatBillingNote (già nel meta).
      // `margin_global` aggrega per periodo: la riga si registra senza IVA
      // esposta e l'aggregatore (`pos-vat-margin-period.service`) calcola
      // l'IVA dovuta sul margine periodico.
      return noExposed();
    }

    case 'margin_analytic': {
      const purchasePrice = parsed.purchasePriceUnit;
      if (purchasePrice == null || !Number.isFinite(purchasePrice) || purchasePrice < 0) {
        throw new Error(
          'margin_analytic richiede purchasePriceUnit valorizzato (>=0). ' +
            'Configurare il prezzo di acquisto sulla riga prima di salvare.',
        );
      }
      // Prezzo cliente lordo (quello esposto in fattura/scontrino).
      const lineGross = q > 0 ? r2(q * up * factor) : 0;
      const discountAmount = q > 0 ? r2(q * up * (discountPct / 100)) : 0;
      // Margine = max(0, prezzo_lordo - prezzo_acquisto).
      const totalPurchase = q > 0 ? r2(q * purchasePrice) : 0;
      const marginRaw = lineGross - totalPurchase;
      const margin = marginRaw > 0 ? r2(marginRaw) : 0;
      // IVA scorporata DAL MARGINE (non esposta in fattura).
      const marginVat = ratePct > 0 ? r2(margin * ratePct / (100 + ratePct)) : 0;
      return {
        mode: 'margin_analytic',
        effectiveRate: 0,
        natureCode: meta.nature_code ?? 'N5',
        vatBillingNote: meta.vat_billing_note ?? null,
        unitPriceGross: r4(up),
        unitPriceNet: r4(up),
        vatAmountUnit: 0,
        lineGross,
        lineNet: lineGross,
        lineVat: 0,
        discountAmount,
        margin,
        marginVat,
      };
    }

    case 'margin_forfait': {
      const forfaitPct = meta.margin_forfait_pct ?? 0;
      if (!(forfaitPct > 0 && forfaitPct <= 100)) {
        throw new Error(
          `margin_forfait richiede margin_forfait_pct in (0,100]; ricevuto ${forfaitPct}.`,
        );
      }
      const lineGross = q > 0 ? r2(q * up * factor) : 0;
      const discountAmount = q > 0 ? r2(q * up * (discountPct / 100)) : 0;
      const margin = r2(lineGross * forfaitPct / 100);
      const marginVat = ratePct > 0 ? r2(margin * ratePct / (100 + ratePct)) : 0;
      return {
        mode: 'margin_forfait',
        effectiveRate: 0,
        natureCode: meta.nature_code ?? 'N5',
        vatBillingNote: meta.vat_billing_note ?? null,
        unitPriceGross: r4(up),
        unitPriceNet: r4(up),
        vatAmountUnit: 0,
        lineGross,
        lineNet: lineGross,
        lineVat: 0,
        discountAmount,
        margin,
        marginVat,
      };
    }

    default: {
      // Exhaustive guard.
      const _never: never = meta.calculation_mode;
      throw new Error(`Modalità calcolo IVA non gestita: ${_never as string}`);
    }
  }
}

/**
 * Aggrega più risultati riga per `effectiveRate`, separando le modalità a
 * IVA non esposta (categoria `nature_code`) per i `DatiRiepilogo` SDI.
 */
export interface VatAggregateGroup {
  effectiveRate: number;
  natureCode: string | null;
  imponibile: number;
  iva: number;
  totale: number;
}
export interface VatAggregateResult {
  byGroup: VatAggregateGroup[];
  imponibile: number;
  iva: number;
  totale: number;
  marginPeriodImponibile: number;
  marginPeriodVat: number;
}

export function aggregateVatLines(lines: VatCalculationResult[]): VatAggregateResult {
  const m = new Map<string, VatAggregateGroup>();
  let marginPeriodImponibile = 0;
  let marginPeriodVat = 0;
  for (const l of lines) {
    const key = `${l.effectiveRate}|${l.natureCode ?? ''}`;
    const cur = m.get(key) || {
      effectiveRate: l.effectiveRate,
      natureCode: l.natureCode,
      imponibile: 0,
      iva: 0,
      totale: 0,
    };
    cur.imponibile = r2(cur.imponibile + l.lineNet);
    cur.iva = r2(cur.iva + l.lineVat);
    cur.totale = r2(cur.totale + l.lineGross);
    m.set(key, cur);
    if (l.mode === 'margin_analytic' || l.mode === 'margin_forfait') {
      marginPeriodImponibile = r2(marginPeriodImponibile + l.margin - l.marginVat);
      marginPeriodVat = r2(marginPeriodVat + l.marginVat);
    }
  }
  const byGroup = Array.from(m.values()).sort((a, b) => b.effectiveRate - a.effectiveRate);
  const tot = byGroup.reduce(
    (a, x) => ({
      imponibile: r2(a.imponibile + x.imponibile),
      iva: r2(a.iva + x.iva),
      totale: r2(a.totale + x.totale),
    }),
    { imponibile: 0, iva: 0, totale: 0 },
  );
  return { byGroup, ...tot, marginPeriodImponibile, marginPeriodVat };
}

/**
 * Helper: dato il `calculation_mode`, indica se la riga richiede prezzo
 * di acquisto a livello di service (validation hard).
 */
export function requiresPurchasePrice(mode: VatCalculationMode): boolean {
  return mode === 'margin_analytic';
}

/**
 * Helper: dato il `calculation_mode`, indica se la riga concorre alla
 * formazione del volume d'affari ai fini IVA (per liquidazione/dichiarazione).
 * Art.15 NON concorre. Tutte le altre sì.
 */
export function contributesToVolumeAffari(mode: VatCalculationMode): boolean {
  return mode !== 'excluded_art15';
}
